import React from 'react';
import { Check, Key, Mail, Eye, EyeOff, X, Loader2 } from 'lucide-react';

const SecuritySection = ({
  providers,
  hasEmailProvider,
  showPasswordChange,
  setShowPasswordChange,
  passwordData,
  setPasswordData,
  passwordMessage,
  passwordChangeLoading,
  handlePasswordChange,
  linkEmailPassword,
  setLinkEmailPassword,
  emailPasswordData,
  setEmailPasswordData,
  setPasswordMessage,
  handleLinkEmailPassword,
  showPasswords,
  setShowPasswords,
  user,
  setShowDeleteConfirmation
}) => {
  const getProviderDisplayName = (provider) => {
    switch(provider) {
      case 'google':
        return 'Google';
      case 'github':
        return 'GitHub';
      case 'kakao':
        return 'Kakao';
      case 'email':
        return '이메일/비밀번호';
      default:
        return provider;
    }
  };

  return (
    <>
      <div className="mt-6 pt-6 border-t">
        <h3 className="text-lg font-semibold mb-4">보안 설정</h3>
        
        <div className="space-y-4">
          {/* 연결된 로그인 방법 표시 */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-2">연결된 로그인 방법</p>
            <div className="space-y-1">
              {providers.map((provider) => (
                <div key={provider} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>{getProviderDisplayName(provider)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 이메일 로그인이 있는 경우 비밀번호 변경 표시 */}
          {hasEmailProvider() && (
            <div>
              {!showPasswordChange ? (
                <div>
                  <button
                    onClick={() => setShowPasswordChange(true)}
                    className="btn btn-secondary btn-md w-full"
                  >
                    <Key className="w-5 h-5" />
                    비밀번호 변경
                  </button>
                  <p className="text-xs text-gray-500 mt-2">새로운 비밀번호로 변경합니다</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">비밀번호 변경</h4>
                    <button
                      onClick={() => {
                        setShowPasswordChange(false);
                        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        setPasswordMessage({ type: '', text: '' });
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div>
                    <label className="label">현재 비밀번호</label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        className="input pr-10"
                        placeholder="현재 비밀번호를 입력하세요"
                        disabled={passwordChangeLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                      >
                        {showPasswords.current ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="label">새 비밀번호</label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                        className="input pr-10"
                        placeholder="최소 6자 이상"
                        disabled={passwordChangeLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                      >
                        {showPasswords.new ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="label">비밀번호 확인</label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="input pr-10"
                        placeholder="비밀번호를 다시 입력하세요"
                        disabled={passwordChangeLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                      >
                        {showPasswords.confirm ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {passwordMessage.text && (
                    <div className={`text-sm ${
                      passwordMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {passwordMessage.text}
                    </div>
                  )}
                  
                  <button
                    onClick={handlePasswordChange}
                    disabled={passwordChangeLoading}
                    className="btn btn-secondary btn-md w-full"
                  >
                    {passwordChangeLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    비밀번호 변경
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* OAuth 로그인만 있고 이메일 로그인이 없는 경우 */}
          {!hasEmailProvider() && providers.length > 0 && (
            <div>
              {!linkEmailPassword ? (
                <div>
                  <button
                    onClick={() => setLinkEmailPassword(true)}
                    className="btn btn-secondary btn-md w-full"
                  >
                    <Mail className="w-5 h-5" />
                    이메일/비밀번호 로그인 추가
                  </button>
                  <p className="text-xs text-gray-500 mt-2">이메일과 비밀번호로도 로그인할 수 있게 설정합니다</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">이메일/비밀번호 설정</h4>
                    <button
                      onClick={() => {
                        setLinkEmailPassword(false);
                        setEmailPasswordData({ password: '', confirmPassword: '' });
                        setPasswordMessage({ type: '', text: '' });
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                    <p>현재 이메일 주소: <strong>{user?.email}</strong></p>
                    <p className="mt-1">이 이메일로 비밀번호를 설정하면 이메일/비밀번호로도 로그인할 수 있습니다.</p>
                  </div>
                  
                  <div>
                    <label className="label">비밀번호</label>
                    <div className="relative">
                      <input
                        type={showPasswords.emailPassword ? "text" : "password"}
                        value={emailPasswordData.password}
                        onChange={(e) => setEmailPasswordData(prev => ({ ...prev, password: e.target.value }))}
                        className="input pr-10"
                        placeholder="최소 6자 이상"
                        disabled={passwordChangeLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, emailPassword: !prev.emailPassword }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                      >
                        {showPasswords.emailPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="label">비밀번호 확인</label>
                    <div className="relative">
                      <input
                        type={showPasswords.emailConfirm ? "text" : "password"}
                        value={emailPasswordData.confirmPassword}
                        onChange={(e) => setEmailPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="input pr-10"
                        placeholder="비밀번호를 다시 입력하세요"
                        disabled={passwordChangeLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, emailConfirm: !prev.emailConfirm }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                      >
                        {showPasswords.emailConfirm ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {passwordMessage.text && (
                    <div className={`text-sm ${
                      passwordMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {passwordMessage.text}
                    </div>
                  )}
                  
                  <button
                    onClick={handleLinkEmailPassword}
                    disabled={passwordChangeLoading}
                    className="btn btn-secondary btn-md w-full"
                  >
                    {passwordChangeLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    이메일/비밀번호 추가
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 계정 관리 섹션 - 최소화된 디자인 */}
      <div className="mt-8 pt-4 border-t border-gray-100">
        <button
          onClick={() => setShowDeleteConfirmation(true)}
          className="text-xs text-gray-400 hover:text-red-600 transition-colors duration-200"
        >
          계정 탈퇴
        </button>
      </div>
    </>
  );
};

export default SecuritySection;
